import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { DownloadIcon } from "@components/Icons";
import { TableWithPagination } from "@components/Tables";
import PrimaryButton from "@components/Buttons/PrimaryButton";
import OutlineButton from "@components/Buttons/OutlineButton";
import { formatDateTime, removeSpaces, getAppLanguage } from "../../utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { PermissionContext } from "@contexts/PermissionContext";
import AddVerificationReportModal from "./AddVerificationReportModal";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import { useNavigate } from "react-router";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants";
import dayjs from "dayjs";
import { PROGRESS_VERIFICATION_REPORT_STATUS } from "@constants/verificationReport";
import ExportSbomReportModal from "./ExportSbomReportModal";
import { buildSbomDownloadPayload, isSbomHtmlReadableExport, resolveSbomExportLanguage } from "@constants/sbom";
import {
  openSbomReadableViewOrToast,
  SBOM_READABLE_VIEW_SOURCE,
} from "@utils/sbomReadableView";

const DROPDOWN_GAP = 4;
/** Fallback before menu is measured (3 SBOM options ≈ py-1 + 3×py-2.5 items). */
const DROPDOWN_HEIGHT_FALLBACK = 140;

const VerificationReportInfo = ({
  projectId,
  projectManagerId,
  verificationVersion,
  handleGetVerificationReportForPopup,
  verificationReportsData = [],
  verificationToolCd = "CLARITY",
  "data-testid": dataTestId = "verification-report-info",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const clientConfig = useClientConfig();
  const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));
  const [showAddReportModal, setShowAddReportModal] = useState(false);
  const [downloadingReports, setDownloadingReports] = useState({}); // Track downloading state per report
  const [exportReport, setExportReport] = useState(null); // Report row targeted by the Export SBOM modal
  const { canManageProjectWorkflow } = useContext(PermissionContext);
  const canManageWorkflow = canManageProjectWorkflow(projectManagerId);
  const navigate = useNavigate();

  // Fetch verification reports on component mount
  useEffect(() => {
    if (handleGetVerificationReportForPopup) {
      handleGetVerificationReportForPopup();
    }
  }, []); // Only run once on mount

  const columns = [
    "verificationReportId",
    "verificationReportName",
    "verificationReportStatusCdName",
    "verificationReportVersion",
    "createrName",
    "createdAt",
    "verificationReport",
    "noticeInfo",
  ];

  const [currentNoticeInfoSeq, setCurrentNoticeInfoSeq] = useState(null);
  const [currentNoticeInfoData, setCurrentNoticeInfoData] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRefs = useRef({});
  const buttonRefs = useRef({});
  const menuRef = useRef(null);

  const updateDropdownPosition = (buttonEl, menuEl) => {
    if (!buttonEl) return;

    const rect = buttonEl.getBoundingClientRect();
    // fixed positioning uses viewport coords — do not add scrollY/scrollX
    const dropdownHeight = menuEl?.offsetHeight || DROPDOWN_HEIGHT_FALLBACK;
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
    const spaceAbove = rect.top - DROPDOWN_GAP;
    // Flip up when not enough room below, preferring the side with more space
    const shouldPositionAbove =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: shouldPositionAbove
        ? rect.top - dropdownHeight - DROPDOWN_GAP
        : rect.bottom + DROPDOWN_GAP,
      left: rect.left,
    });
  };

  const noticeInfoDownloadText = useMutationData(
    (noticeInfoSeq) => `${queryKeys.noticeInfoDownloadText}?noticeInfoSeq=${noticeInfoSeq}`,
    {
      method: "GET",
      responseType: "blob",
    },
    {
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `NOTICE_INFO_${currentNoticeInfoData?.projectId || ""}_${currentNoticeInfoData?.noticeInfo?.noticeInfoId || ""}_${currentNoticeInfoSeq}.txt`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setCurrentNoticeInfoSeq(null); // Reset after use
        setCurrentNoticeInfoData(null); // Reset after use
      },
      onError: (error) => {
        setCurrentNoticeInfoSeq(null); // Reset on error too
        setCurrentNoticeInfoData(null); // Reset on error too
      },
    },
  );

  const noticeInfoDownloadHtml = useMutationData(
    (noticeInfoSeq) => `${queryKeys.noticeInfoDownloadHtml}?noticeInfoSeq=${noticeInfoSeq}`,
    {
      method: "GET",
      responseType: "blob",
    },
    {
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `NOTICE_INFO_${currentNoticeInfoData?.projectId || ""}_${currentNoticeInfoData?.noticeInfo?.noticeInfoId || ""}_${currentNoticeInfoSeq}.html`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setCurrentNoticeInfoSeq(null); // Reset after use
        setCurrentNoticeInfoData(null); // Reset after use
      },
      onError: (error) => {
        setCurrentNoticeInfoSeq(null); // Reset on error too
        setCurrentNoticeInfoData(null); // Reset on error too
      },
    },
  );

  // Close dropdown when clicking outside and update position on scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId !== null) {
        const ref = dropdownRefs.current[openDropdownId];
        if (ref && !ref.contains(event.target)) {
          setOpenDropdownId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openDropdownId === null) return;
      const buttonEl = buttonRefs.current[openDropdownId];
      updateDropdownPosition(buttonEl, menuRef.current);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true); // Use capture phase to catch all scroll events
    window.addEventListener("resize", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [openDropdownId]);

  // Re-measure after menu mounts so flip uses real height (3 SBOM items > old 90px estimate)
  useLayoutEffect(() => {
    if (openDropdownId === null) return;
    const buttonEl = buttonRefs.current[openDropdownId];
    updateDropdownPosition(buttonEl, menuRef.current);
  }, [openDropdownId]);


  const handleDownloadNoticeInfo = (noticeInfoSeq, rowData, format = "text") => {
    setCurrentNoticeInfoSeq(noticeInfoSeq);
    setCurrentNoticeInfoData(rowData);
    if (format === "html") {
      noticeInfoDownloadHtml.mutate(noticeInfoSeq);
    } else {
      noticeInfoDownloadText.mutate(noticeInfoSeq);
    }
    setOpenDropdownId(null); // Close dropdown after selection
  };

  const handleFormatSelect = (format, noticeInfoSeq, rowData) => {
    handleDownloadNoticeInfo(noticeInfoSeq, rowData, format);
  };

  const toggleDropdown = (rowId) => {
    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
      return;
    }

    const buttonEl = buttonRefs.current[rowId];
    updateDropdownPosition(buttonEl, null);
    setOpenDropdownId(rowId);
  };


  // Download verification report SBOM mutation
  const downloadVerificationReportSbom = useMutationData(
    (data) => {
      const typeParam = data.sbomType ? `&sbomType=${encodeURIComponent(data.sbomType)}` : "";
      const baseUrl = `/${queryKeys.downloadVerificationReportSbom}?verificationReportSeq=${data.verificationReportSeq}${typeParam}`;
      const formatParam = data.format ? `&format=${encodeURIComponent(data.format)}` : "";
      const languageParam = `&language=${encodeURIComponent(resolveSbomExportLanguage(data.format, getAppLanguage()))}`;
      return data.includeVex
          ? `${baseUrl}&includeVex=Y${formatParam}${languageParam}`
          : `${baseUrl}&includeVex=N${formatParam}${languageParam}`;
    },
    { method: "GET", responseType: "arraybuffer" },
    {
      onSuccess: (data, variables) => {
        // Generate download file name
        const currentTimestamp = dayjs().format("YYYYMMDD");
        const reportName = variables.fetchedVerificationData?.verificationReportName
          ? removeSpaces(variables.fetchedVerificationData.verificationReportName)
          : "verification_report";
        const sbomName = removeSpaces(variables.sbomName);
        const verificationToolCd = variables.fetchedVerificationData?.verificationToolCd || "";
        const { blob, extension } = buildSbomDownloadPayload(data, variables.format);

        let downloadFileName;
        if (variables.format === "CSAF") {
          downloadFileName = `${reportName}_${verificationToolCd}_CSAF_${currentTimestamp}.${extension}`;
        } else {
          downloadFileName = `${reportName}_${verificationToolCd}_${sbomName}_${currentTimestamp}.${extension}`;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Reset downloading state
        setDownloadingReports(prev => ({
          ...prev,
          [variables.verificationReportSeq]: false
        }));
      },
      onError: (error, variables) => {
        console.error("Error during download:", error);
        ToastConstant(TOAST.error, t("toast.download.error.title"), t("toast.download.error.message"));

        // Reset downloading state
        setDownloadingReports(prev => ({
          ...prev,
          [variables.verificationReportSeq]: false
        }));
      },
    },
  );

  const handleDownloadFormatSelect = (selectedOption, report, includeVex = false, format = "JSON") => {
    if (!report || !report.verificationReportSeq) return;
    if (format !== "CSAF" && !selectedOption) return;

    // Check if report is in DISTRIBUTION_DONE status
    if (report.verificationReportStatusCd !== PROGRESS_VERIFICATION_REPORT_STATUS.DISTRIBUTION_DONE) {
      ToastConstant(TOAST.error, t("toast.download.error.title"), t("toast.download.error.message"));
      return;
    }

    // Set downloading state
    setDownloadingReports(prev => ({
      ...prev,
      [report.verificationReportSeq]: true
    }));

    if (format === "CSAF") {
      downloadVerificationReportSbom.mutate({
        verificationReportSeq: report.verificationReportSeq,
        sbomName: "CSAF",
        fetchedVerificationData: report,
        includeVex: true,
        isVexPreferenceOn: isVexOn,
        format,
      });
      return;
    }

    const targetSbomName = selectedOption.sbomName || selectedOption.label;
    downloadVerificationReportSbom.mutate({
      verificationReportSeq: report.verificationReportSeq,
      sbomType: selectedOption.value,
      sbomName: targetSbomName,
      fetchedVerificationData: report,
      includeVex,
      isVexPreferenceOn: isVexOn,
      format,
    });
  };

  const handleExportSbomReport = async (selection) => {
    const { type, format, includeVex } = selection || {};
    if (!type || !exportReport) return;

    if (isSbomHtmlReadableExport(type)) {
      if (exportReport.verificationReportStatusCd !== PROGRESS_VERIFICATION_REPORT_STATUS.DISTRIBUTION_DONE) {
        ToastConstant(TOAST.error, t("toast.download.error.title"), t("toast.download.error.message"));
        setExportReport(null);
        return;
      }

      const reportSeq = exportReport.verificationReportSeq;
      const reportTitle = exportReport.verificationReportName || "";
      setDownloadingReports((prev) => ({
        ...prev,
        [reportSeq]: true,
      }));
      setExportReport(null);

      try {
        openSbomReadableViewOrToast(
          {
            sourceType: SBOM_READABLE_VIEW_SOURCE.VERIFICATION,
            params: {
              verificationReportSeq: reportSeq,
              includeVex: includeVex ? "Y" : "N",
            },
            title: reportTitle,
          },
          "toast.download.error.title",
        );
      } finally {
        setDownloadingReports((prev) => ({
          ...prev,
          [reportSeq]: false,
        }));
      }
      return;
    }

    handleDownloadFormatSelect(type, exportReport, includeVex, format);
    setExportReport(null);
  };

  const renderStatusCell = (status) => {
    const STATUS = {
      "DISTRIBUTION_COMPLETED": "Distribution Completed",
      "WRITING": "Writing...",
      "REVIEWING": "Reviewing...",
    }

    const statusStyles = {
      [STATUS.DISTRIBUTION_COMPLETED]: colors.textSuccess,
      [STATUS.WRITING]: colors.textStatusAnalysis,
      [STATUS.REVIEWING]: colors.textStatusReview,
    };
    const statusColorDot = {
      [STATUS.DISTRIBUTION_COMPLETED]: colors.bgStatusSuccess,
      [STATUS.WRITING]: colors.bgStatusAnalysis,
      [STATUS.REVIEWING]: colors.bgStatusReview,
    };
    return (
      <span
        className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold ${statusStyles[status] || `${colors.bgStatusTagNeutral} ${colors.textPrimary}`}`}
      >
        <div className={`mr-2 h-2 w-2 shrink-0 rounded-full ${statusColorDot[status]}`}></div>
        {t(`project.verification-report.verification-report-status-${status?.toLowerCase() || ""}`, status)}
      </span>
    );
  };

  const columnConfig = {
    verificationReportId: {
      headerRender: () => t("project.verification-report.verification-report-id"),
      truncate: false,
      customRender: (value) => (
        <PrimaryButton
          size="sm"
          onClick={() => handleReportIdClick(value)}
          className="!min-w-0 max-w-full px-2.5"
          title={value}
        >
          {value}
        </PrimaryButton>
      ),
      style: { minWidth: "140px", textAlign: "center" },
    },
    verificationReportName: {
      headerRender: () => t("project.verification-report.verif-report-name"),
      customRender: (value) => (
        <span className="block truncate" title={value}>
          {value}
        </span>
      ),
      style: { minWidth: "110px", maxWidth: "180px", textAlign: "center" },
    },
    verificationReportStatusCdName: {
      headerRender: () => t("project.verification-report.verif-report-status"),
      customRender: (value, row) => renderStatusCell(value),
      truncate: false,
      style: { minWidth: "150px", textAlign: "center" },
    },
    verificationReportVersion: {
      headerRender: () => t("project.verification-report.verif-report-version"),
      style: { minWidth: "72px", textAlign: "center" },
    },
    createrName: {
      headerRender: () => t("common.creator"),
      customRender: (value, row) => (
        <UserNameWithId name={row.createrName} id={row.createrId} className="block truncate" />
      ),
      style: { minWidth: "80px", maxWidth: "120px" },
    },
    createdAt: {
      headerRender: () => t("common.create-at"),
      customRender: (value) => {
        const formatted = formatDateTime(value);
        return (
          <span className="block whitespace-nowrap" title={formatted}>
            {formatted}
          </span>
        );
      },
      truncate: false,
      style: { minWidth: "155px", textAlign: "center" },
    },
    verificationReport: {
      headerRender: () => t("project.verification-report.verif-report-rep"),
      customRender: (value, row) => {
        const isDownloading = downloadingReports[row.verificationReportSeq] || false;
        const isDistributionDone = row.verificationReportStatusCd === PROGRESS_VERIFICATION_REPORT_STATUS.DISTRIBUTION_DONE;

        if (!isDistributionDone) {
          return (
            <span className={`text-sm ${colors.textSecondary}`}>-</span>
          );
        }

        return (
          <div className="flex justify-center">
            <PrimaryButton
              size="sm"
              disabled={isDownloading}
              onClick={() => setExportReport(row)}
              data-testid={`${dataTestId}-export-${row.verificationReportSeq}`}
            >
              <span className="inline-flex items-center gap-1">
                {t("project.verification-report.export-sbom-report")}
                <DownloadIcon className="h-4 w-4 shrink-0" />
              </span>
            </PrimaryButton>
          </div>
        );
      },
      truncate: false,
      style: { minWidth: "120px", textAlign: "center" },
    },
    noticeInfo: {
      headerRender: () => t("project.verification-report.notice-info"),
      customRender: (value, row) => {
        if (!value) return "-";
        const rowId = row.noticeInfo?.noticeInfoSeq || row.verificationReportSeq;
        const isDropdownOpen = openDropdownId === rowId;

        return value.noticeInfoWriteStatusCd === "DONE" ? (
          <div className="flex justify-center" style={{ overflow: 'visible', position: 'relative' }}>
            <div
              className="relative"
              style={{ overflow: 'visible' }}
              ref={(el) => {
                if (el) {
                  dropdownRefs.current[rowId] = el;
                } else {
                  delete dropdownRefs.current[rowId];
                }
              }}
            >
              <button
                ref={(el) => {
                  if (el) {
                    buttonRefs.current[rowId] = el;
                  } else {
                    delete buttonRefs.current[rowId];
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDropdown(rowId, e);
                }}
                className={`inline-flex shrink-0 items-center justify-center gap-1 rounded-md p-2 transition-colors ${colors.textActive} hover:opacity-80 cursor-pointer`}
                title={t("project.verification-report.download-notice-info")}
                aria-label={t("common.download")}
              >
                <span className="whitespace-nowrap text-sm font-medium">
                  {t("common.download")}
                </span>
                <DownloadIcon className="h-4 w-4 shrink-0" />
                <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown menu - rendered outside table overflow */}
              {isDropdownOpen && (
                <div
                  ref={menuRef}
                  className={`fixed w-36 rounded-lg shadow-xl ${colors.bgModal} ${colors.borderPrimary} border z-[9999] overflow-hidden`}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    zIndex: 9999,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFormatSelect("text", row.noticeInfo.noticeInfoSeq, row);
                      }}
                      className={`group block w-full text-left px-4 py-2.5 text-sm ${colors.textPrimary} transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:shadow-sm relative overflow-hidden hover:bg-gradient-to-r hover:from-[#1EC6FF] hover:to-[#367FFF] hover:text-black`}
                      style={{
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <span className="relative z-10 flex items-center font-medium">
                        <svg className="mr-2 h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t("project.notice-infor.download-by-text", "Text")}
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFormatSelect("html", row.noticeInfo.noticeInfoSeq, row);
                      }}
                      className={`group block w-full text-left px-4 py-2.5 text-sm ${colors.textPrimary} transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:shadow-sm relative overflow-hidden hover:bg-gradient-to-r hover:from-[#1EC6FF] hover:to-[#367FFF] hover:text-black`}
                      style={{
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <span className="relative z-10 flex items-center font-medium">
                        <svg className="mr-2 h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                        {t("project.notice-infor.download-by-html", "Html")}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <span className={`text-sm font-medium ${colors.textWarning}`}>-</span>
        );
      },
      truncate: false,
      style: { minWidth: "120px", textAlign: "center" },
    },
  };

  const handleReportIdClick = (reportId) => {
    const report = verificationReportsData.find((report) => report.verificationReportId === reportId);
    if (report) {
      navigate(`/verification-report/${report.verificationReportSeq}`);
    }
  };

  const handleAddNewReport = () => {
    setShowAddReportModal(true);
  };

  const handleCloseAddReportModal = () => {
    setShowAddReportModal(false);
  };



  return (
    <>
      <div className={`${colors.bgPrimary} rounded-lg p-4`} data-testid={dataTestId}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4" data-testid={`${dataTestId}-header`}>
          <h2 className={`${colors.textPrimary} text-lg font-semibold uppercase`} data-testid={`${dataTestId}-title`}>
            {t("project.verification-report.verification-report")}
          </h2>
          {canManageWorkflow && (
            <OutlineButton
              data-testid={`${dataTestId}-add-button`}
              onClick={handleAddNewReport}
              className="flex items-center gap-2"
            >
              {t("project.verification-report.add-new-report")}
            </OutlineButton>
          )}

        </div>

        {/* Table */}
        <div data-testid={`${dataTestId}-table`}>
          <TableWithPagination
            columns={columns}
            data={verificationReportsData}
            columnConfig={columnConfig}
            showPagination={false}
            rowKey="verificationReportSeq"
            tableStyle="min-w-[52rem]"
            resizable={false}
            paddingYHeader="py-3"
          />
        </div>
      </div>

      {/* Add Verification Report Modal */}
      <AddVerificationReportModal
        isOpen={showAddReportModal}
        onClose={handleCloseAddReportModal}
        onSave={() => {
          // Refresh the verification reports list
          if (handleGetVerificationReportForPopup) {
            handleGetVerificationReportForPopup();
          }
        }}
        projectId={projectId}
        verificationVersion={verificationVersion}
        verificationToolCd={verificationToolCd}
      />

      {/* Export SBOM Report Modal */}
      <ExportSbomReportModal
        isOpen={!!exportReport}
        onClose={() => setExportReport(null)}
        exporting={exportReport ? !!downloadingReports[exportReport.verificationReportSeq] : false}
        onExport={handleExportSbomReport}
      />

    </>
  );
};

export default VerificationReportInfo; 